import { KnotPalette } from "./knotThemes";

/**
 * Раскладка палитры Knot по цветовым ролям Material 3.
 *
 * Весь интерфейс чата построен на переменных `--md-sys-color-*`, которые обычно
 * генерируются из одного «зерна» алгоритмом Material You. Мы эти же роли
 * заполняем вручную из шести якорей Knot — так чат получает ровно те цвета, что
 * и Knot, а не «похожие».
 *
 * ⚠️ Заполняем ВСЕ роли до единой. Пропущенная роль — это не «чуть другой
 * оттенок», а невалидная CSS-переменная: элемент, который её использует,
 * останется с цветом предыдущей темы или вовсе без цвета.
 *
 * Таблица соответствия (она же — ответ на вопрос «почему так»):
 *   deep    → самая нижняя поверхность (боковые панели, surface-dim)
 *   bg      → основной фон (surface)
 *   raised  → приподнятое (поля ввода, наведение)
 *   border  → границы (outline)
 *   muted   → приглушённый текст (on-surface-variant)
 *   fg      → основной текст (on-surface)
 *   accent  → primary, accentFg → on-primary
 *   info/warn/danger → secondary / tertiary / error
 */

type Rgb = [number, number, number];

function hexToRgb(hex: string): Rgb {
  const value = hex.replace("#", "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value.slice(0, 6);
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

function rgbToHex([r, g, b]: Rgb): string {
  const part = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, "0");
  return `#${part(r)}${part(g)}${part(b)}`;
}

/** Смешивание двух цветов: t = 0 → первый, t = 1 → второй. */
function mix(a: string, b: string, t: number): string {
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  return rgbToHex([r1 + (r2 - r1) * t, g1 + (g2 - g1) * t, b1 + (b2 - b1) * t]);
}

/** Относительная яркость по WCAG — по ней выбирается читаемый текст на заливке. */
function luminance(hex: string): number {
  const channel = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/**
 * Текст, читаемый на заданной заливке. Считаем яркость, а не «тёмная ли тема»:
 * жёлтая плашка предупреждения в тёмной теме требует ЧЁРНОГО текста, и правило
 * «в тёмной теме текст светлый» дало бы белое по жёлтому.
 */
function readableOn(background: string): string {
  return luminance(background) > 0.45 ? "#111111" : "#ffffff";
}

/**
 * Полный набор ролей Material 3 из палитры Knot.
 * Ключи и их состав — из `MaterialColours` в materialTheme.ts.
 */
export function createKnotColourScheme(palette: KnotPalette, darkMode: boolean) {
  const { deep, bg, raised, border, muted, fg, accent, accentFg } = palette;

  // Направление «глубже/светлее» зависит от вида темы: у тёмных контейнеры
  // темнеют к deep, у светлых — наоборот. Оттого и разные точки смешивания.
  const containerTint = darkMode ? 0.28 : 0.16;

  const secondary = palette.info;
  const tertiary = palette.warn;
  const error = palette.danger;

  return {
    primary: accent,
    "on-primary": accentFg,
    "primary-container": mix(bg, accent, containerTint),
    "on-primary-container": fg,

    secondary,
    "on-secondary": readableOn(secondary),
    "secondary-container": mix(bg, secondary, containerTint),
    "on-secondary-container": fg,

    tertiary,
    "on-tertiary": readableOn(tertiary),
    "tertiary-container": mix(bg, tertiary, containerTint),
    "on-tertiary-container": fg,

    error,
    "on-error": readableOn(error),
    "error-container": mix(bg, error, containerTint),
    "on-error-container": fg,

    // «Fixed» роли не меняются между светлой и тёмной темой — их держим на
    // акценте и его соседях, чтобы фирменный цвет оставался узнаваемым.
    "primary-fixed": mix("#ffffff", accent, 0.75),
    "primary-fixed-dim": accent,
    "on-primary-fixed": mix("#000000", accent, 0.25),
    "on-primary-fixed-variant": mix("#000000", accent, 0.45),
    "secondary-fixed": mix("#ffffff", secondary, 0.75),
    "secondary-fixed-dim": secondary,
    "on-secondary-fixed": mix("#000000", secondary, 0.25),
    "on-secondary-fixed-variant": mix("#000000", secondary, 0.45),
    "tertiary-fixed": mix("#ffffff", tertiary, 0.75),
    "tertiary-fixed-dim": tertiary,
    "on-tertiary-fixed": mix("#000000", tertiary, 0.25),
    "on-tertiary-fixed-variant": mix("#000000", tertiary, 0.45),

    "surface-dim": deep,
    surface: bg,
    "surface-bright": raised,

    "surface-container-lowest": deep,
    "surface-container-low": mix(deep, bg, 0.5),
    "surface-container": bg,
    "surface-container-high": raised,
    "surface-container-highest": mix(raised, border, 0.5),

    "on-surface": fg,
    "on-surface-variant": muted,
    outline: border,
    "outline-variant": mix(border, bg, 0.5),

    // Инверсия — для всплывающих подсказок и «снэкбаров»: они рисуются
    // противоположной поверхностью, иначе теряются на фоне.
    "inverse-surface": fg,
    "inverse-on-surface": bg,
    "inverse-primary": mix(accent, bg, 0.35),

    scrim: "#000000",
    shadow: "#000000",
  };
}
